import { displayMode_Form, displayMode_FreeText, displayMode_Search, FindingType_Enumerated, FindingType_Numeric, FindingType_Textual, stepStatus_NotSubmitted } from '@app/advisor-shared';
import { _keys, currentTimeMillis, filterDuplicates, sortArray } from '@nu-art/ts-common';
import { ModuleFE_Advisor_AdvisorSnippet } from '../../modules/static-modules/advisor-context.js';
import { valueID_Pos, valueID_PosNeg, valueID_Yes, valueID_YesNo } from '@app/hcs-shared';
import { AdvisorConfigKeyFE_HPIVarID } from '../../modules/static-modules/app-config.js';
import { ModuleFE_RuntimeFinding } from '../../_entity/runtime/runtime-finding/index.js';
//######################### Data Generation - Wizard Step #########################
const getDisplayMode = (step, existing) => {
    if (existing?.displayMode)
        return existing.displayMode;
    //default display mode to free text if it can, then search if it can be a search, otherwise form
    if (step.freeTextData.isActive)
        return displayMode_FreeText;
    if (step.searchData.isActive)
        return displayMode_Search;
    if (step.formData.isActive)
        return displayMode_Form;
    return displayMode_Search;
};
const getSnippets = (step, displayMode) => {
    switch (displayMode) {
        case displayMode_Form:
            return step.formData.overwriteSnippets ? step.formData.snippets : step.snippets;
        case displayMode_Search:
            return step.searchData.overwriteSnippets ? step.searchData.snippets : step.snippets;
        case displayMode_FreeText:
            return step.freeTextData.overwriteSnippets ? step.freeTextData.snippets : step.snippets;
        default:
            return [];
    }
};
export const generatedAdvisorWizard_StepData = (step, index, existing) => {
    if (existing)
        return existing;
    const displayMode = getDisplayMode(step, existing);
    const snippets = {
        [displayMode_Form]: getSnippets(step, displayMode_Form),
        [displayMode_FreeText]: getSnippets(step, displayMode_FreeText),
        [displayMode_Search]: getSnippets(step, displayMode_Search),
    };
    return {
        index,
        status: stepStatus_NotSubmitted,
        displayMode,
        snippets
    };
};
//######################### Data Generation - Free Text #########################
const hpiSnippetId = '050737ad2dba8166b37dae32b7a0ee7b';
const generateAdvisorWizard_FreeTextData = (stepData, existing) => {
    const allFreeTextSnippets = filterDuplicates(stepData.map(step => step.snippets[displayMode_FreeText]).flat());
    return allFreeTextSnippets.reduce((acc, snippet) => {
        //HACK - load latest HPI to the snippet if the id is the known id for the hpi snippet
        if (snippet.snippetId === hpiSnippetId) {
            const hpiVarId = AdvisorConfigKeyFE_HPIVarID.get();
            const latestHPIFinding = sortArray(ModuleFE_RuntimeFinding.cache.filter(f => {
                return f.type === FindingType_Textual && f.variableId === hpiVarId;
            }), f => f.__created, true)[0];
            const existingData = existing?.[snippet.snippetId];
            //Start out the data with the latest HPI value and hasChanges set to false, if the HPI is more recent then the existing data
            if (latestHPIFinding && latestHPIFinding.__updated > (existingData?.updateTimestamp ?? 0)) {
                acc[snippet.snippetId] = {
                    text: latestHPIFinding.value,
                    hasChanges: false,
                    updateTimestamp: latestHPIFinding.__updated,
                    resolvedVariableId: snippet.resolvedVariableId,
                };
            }
            else {
                acc[snippet.snippetId] = existing?.[snippet.snippetId] ?? {
                    text: '',
                    hasChanges: false,
                    updateTimestamp: currentTimeMillis(),
                    resolvedVariableId: snippet.resolvedVariableId
                };
            }
        }
        else
            acc[snippet.snippetId] = existing?.[snippet.snippetId] ?? {
                text: '',
                hasChanges: false,
                updateTimestamp: currentTimeMillis(),
                resolvedVariableId: snippet.resolvedVariableId
            };
        return acc;
    }, {});
};
//######################### Data Generation - Variables #########################
const generateAdvisorWizard_VariableData = (stepData, latestFindingsMap, existing) => {
    const allFormSnippetIds = filterDuplicates(stepData.map(step => step.snippets[displayMode_Search].map(i => i.snippetId)).flat());
    const snippets = ModuleFE_Advisor_AdvisorSnippet.cache.filter(snippet => allFormSnippetIds.includes(snippet._id));
    const allVariableIds = filterDuplicates(snippets.map(snippet => snippet.formVariables ?? []).flat().map(formVar => formVar.id));
    return allVariableIds.reduce((acc, varId) => {
        const data = generateAdvisorWizard_Variable(varId, latestFindingsMap, existing?.[varId]);
        if (data.length)
            acc[varId] = data;
        return acc;
    }, {});
};
const generateAdvisorWizard_Variable = (varId, latestFindingsMap, existing) => {
    const latestFinding = latestFindingsMap[varId];
    //If no findings found for this var, return existing data or an empty array
    if (!latestFinding)
        return existing ?? [];
    //Generate wizard data from the finding
    if (latestFinding.type === FindingType_Enumerated) {
        return [{
                existingFindingId: latestFinding._id,
                isChiefComplaint: latestFinding.isChiefComplaint,
                snippetId: latestFinding.snippetId,
                valueId: latestFinding.valueIds[0],
                attributes: latestFinding.attributes?.reduce((acc, att) => {
                    acc[att.variableId] = att.valueIds;
                    return acc;
                }, {}) ?? {}
            }];
    }
    if (latestFinding.type === FindingType_Numeric) {
        return [{
                existingFindingId: latestFinding._id,
                isChiefComplaint: latestFinding.isChiefComplaint,
                snippetId: latestFinding.snippetId,
                rawValue: latestFinding.value,
                attributes: {},
            }];
    }
    return existing ?? [];
};
export const generateAdvisorWizard_Variable_Initial = (variable, snippet) => {
    //Variable is yes/no, start it out with value "yes"
    if (variable.valueId === valueID_YesNo)
        return [{
                valueId: valueID_Yes,
                isChiefComplaint: snippet.config.produceChiefComplaint,
                snippetId: snippet._id,
                attributes: {},
            }];
    if (variable.valueId === valueID_PosNeg)
        return [{
                valueId: valueID_Pos,
                isChiefComplaint: snippet.config.produceChiefComplaint,
                snippetId: snippet._id,
                attributes: {},
            }];
    //For now return an empty array
    return [];
};
//######################### Data Generation - Wizard #########################
export const generatedAdvisorWizard_Data = (wizard, contextId, contextType, existing) => {
    const stepData = wizard.steps.map((step, index) => generatedAdvisorWizard_StepData(step, index, existing?.stepData?.[index]));
    //Decide what the findings map should look like
    let latestFindingsMap = {};
    if (wizard.config?.loadFindings) {
        //Not loading only RTOrder related findings, load from all latest findings
        if (!wizard.config?.onlyLoadFindingsFromRTOrder)
            latestFindingsMap = ModuleFE_RuntimeFinding.getLatestFindings(true, true);
        else {
            const latestFindings = ModuleFE_RuntimeFinding.getLatestFindings(false, true);
            const relatedFindings = latestFindings.filter(finding => finding.runtimeOrderId === contextId);
            latestFindingsMap = relatedFindings.reduce((map, finding) => {
                map[finding.variableId] = finding;
                return map;
            }, {});
        }
    }
    const freeTextData = generateAdvisorWizard_FreeTextData(stepData, existing?.freeTextData);
    const variableData = generateAdvisorWizard_VariableData(stepData, latestFindingsMap, existing?.variableData);
    //HACK - hpi wizard should start with the latest hpi finding value
    if (wizard.config?.isHpi) {
        const hpiVarId = AdvisorConfigKeyFE_HPIVarID.get();
        const latestHPIFinding = sortArray(ModuleFE_RuntimeFinding.cache.filter(f => {
            return f.type === FindingType_Textual && f.variableId === hpiVarId;
        }), f => f.__created, true)[0];
        const snippetId = _keys(freeTextData)[0];
        if (latestHPIFinding && snippetId) {
            freeTextData[snippetId] = { text: latestHPIFinding.value, hasChanges: false, updateTimestamp: latestHPIFinding.__updated };
        }
    }
    return {
        contextId,
        contextType,
        stepData,
        variableData,
        freeTextData,
    };
};
//# sourceMappingURL=utils.js.map